import { normalizeEqualizer } from './equalizer';
import { EMPTY_CLOUD_STATE, stableTrackId } from './musicCloud';
import type { MusicTrack } from './musicTypes';
import { normalizePlaylists } from './playlists';
import type { ByteSource } from './tagReader';
import type { ImportResult, StoredLibrary } from './musicLibrary';

/**
 * Фонотека в браузере: файлы — в IndexedDB этой страницы, на сервер не уходит ничего.
 *
 * <p>Тот же контракт, что у телефонной версии (`musicLibrary.ts`), только хранилище другое:
 * у страницы нет файловой системы, а `blob:`-ссылка на выбранный файл умирает с вкладкой.
 * IndexedDB держит сами байты и переживает перезагрузку.
 */

const DB_NAME = 'svyaz-music';
const FILES = 'files';
const META = 'meta';

export const AUDIO_MIME_TYPES = ['audio/*', '.flac', '.m4a', '.opus', '.ogg'];

let owner = 'default';

/** Чья фонотека открыта: у каждого аккаунта свои ключи в IndexedDB (см. `musicLibrary.ts`). */
export function setLibraryOwner(userId: string): void {
  owner = userId || 'default';
}

const scoped = (k: string) => `${owner}:${k}`;
export type { ImportResult, StoredLibrary };

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(FILES);
      req.result.createObjectStore(META);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(store: string, mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const req = run(db.transaction(store, mode).objectStore(store));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Ссылки на картинки и звук живут, пока открыта вкладка; создаём их по разу на ключ. */
const blobUrls = new Map<string, string>();

async function blobUrl(key: string): Promise<string | null> {
  const cached = blobUrls.get(key);
  if (cached) {
    return cached;
  }
  const blob = await tx<Blob | undefined>(FILES, 'readonly', (s) => s.get(key));
  if (!blob) {
    return null;
  }
  const url = URL.createObjectURL(blob);
  blobUrls.set(key, url);
  return url;
}

export async function loadLibrary(): Promise<StoredLibrary> {
  const libraryKey = scoped('library');
  const playerKey = scoped('player');
  try {
    const raw = (await tx<Partial<StoredLibrary> | undefined>(META, 'readonly', (s) => s.get(libraryKey))) ?? {};
    const player = await tx<StoredLibrary['player'] | undefined>(META, 'readonly', (s) => s.get(playerKey));
    const tracks = Array.isArray(raw.tracks) ? raw.tracks : [];
    const have = new Set(tracks.map((t) => t.id));
    const ghosts = (Array.isArray(raw.ghosts) ? raw.ghosts : []).filter((g) => g && typeof g.id === 'string' && !have.has(g.id));
    // Обложки подгружаем заранее: `artworkUri` синхронный, как и на телефоне.
    await Promise.all(tracks.map((t) => (t.artworkRef ? blobUrl(t.artworkRef) : null)));
    return {
      tracks,
      ghosts,
      cloud: raw.cloud && typeof raw.cloud.revision === 'number' ? raw.cloud : EMPTY_CLOUD_STATE,
      playlists: normalizePlaylists(raw.playlists, new Set([...have, ...ghosts.map((g) => g.id)])),
      player: player ?? raw.player ?? null,
      equalizer: normalizeEqualizer(raw.equalizer),
    };
  } catch {
    return { tracks: [], playlists: [], ghosts: [], cloud: EMPTY_CLOUD_STATE, player: null, equalizer: normalizeEqualizer(null) };
  }
}

/** Ключи берутся в момент вызова, до первого `await`: см. `saveLibrary` в `musicLibrary.ts`. */
export async function saveLibrary(lib: StoredLibrary): Promise<void> {
  const libraryKey = scoped('library');
  const playerKey = scoped('player');
  await tx(META, 'readwrite', (s) => s.put({ ...lib, player: null }, libraryKey));
  if (lib.player) {
    await tx(META, 'readwrite', (s) => s.put(lib.player, playerKey));
  }
}

export async function savePlayerState(player: NonNullable<StoredLibrary['player']>): Promise<void> {
  const playerKey = scoped('player');
  await tx(META, 'readwrite', (s) => s.put(player, playerKey));
}

function pickFiles(): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = AUDIO_MIME_TYPES.join(',');
    input.multiple = true;
    input.style.display = 'none';
    input.onchange = () => {
      resolve(Array.from(input.files ?? []));
      input.remove();
    };
    // Закрытие окна без выбора `change` не присылает; `cancel` есть в современных браузерах.
    input.addEventListener('cancel', () => {
      resolve([]);
      input.remove();
    });
    document.body.appendChild(input);
    input.click();
  });
}

function fileSource(file: Blob): ByteSource {
  return {
    size: file.size,
    read: async (offset, length) => new Uint8Array(await file.slice(offset, offset + length).arrayBuffer()),
  };
}

export async function pickAndImport(
  known: readonly MusicTrack[],
  onProgress?: (done: number, total: number) => void,
): Promise<ImportResult | null> {
  const files = await pickFiles();
  if (files.length === 0) {
    return null;
  }
  // Разбор тегов нужен только при импорте — грузится по требованию: основной бандл веба
  // упирается в бюджет сборки (`performance` в webpack.config.cjs).
  const { readTags } = await import('./tagReader');
  // Повтор — по тому же ключу, что и id (см. `musicLibrary.ts`).
  const seen = new Set(known.flatMap((t) => [t.id, stableTrackId(t.sourceName, t.sizeBytes)]));
  const result: ImportResult = { added: [], duplicates: 0, failed: 0 };
  let done = 0;
  for (const file of files) {
    try {
      const id = stableTrackId(file.name, file.size);
      if (seen.has(id)) {
        result.duplicates += 1;
        continue;
      }
      const tags = await readTags(fileSource(file), file.name);
      await tx(FILES, 'readwrite', (s) => s.put(file, scoped(`track:${id}`)));
      let artworkRef: string | null = null;
      if (tags.artwork) {
        artworkRef = scoped(`art:${id}`);
        const art = new Blob([Uint8Array.from(tags.artwork.data)], { type: tags.artwork.mime });
        await tx(FILES, 'readwrite', (s) => s.put(art, artworkRef as string));
        await blobUrl(artworkRef);
      }
      seen.add(id);
      result.added.push({
        id,
        title: tags.title ?? file.name,
        artist: tags.artist ?? '',
        album: tags.album ?? '',
        durationSec: tags.durationSec ?? 0,
        fileRef: scoped(`track:${id}`),
        artworkRef,
        format: tags.format,
        replayGainDb: tags.replayGainDb,
        replayGainPeak: tags.replayGainPeak,
        addedAt: Date.now(),
        sourceName: file.name,
        sizeBytes: file.size,
      });
    } catch {
      result.failed += 1;
    } finally {
      done += 1;
      onProgress?.(done, files.length);
    }
  }
  return result;
}

export async function deleteTrackFiles(track: MusicTrack): Promise<void> {
  for (const key of [track.fileRef, track.artworkRef]) {
    if (!key) {
      continue;
    }
    const url = blobUrls.get(key);
    if (url) {
      URL.revokeObjectURL(url);
      blobUrls.delete(key);
    }
    await tx(FILES, 'readwrite', (s) => s.delete(key)).catch(() => undefined);
  }
}

export async function playableUri(track: MusicTrack): Promise<string> {
  const url = await blobUrl(track.fileRef);
  if (!url) {
    throw new Error('track file is missing');
  }
  return url;
}

export function artworkUri(track: MusicTrack | null | undefined): string | null {
  return track?.artworkRef ? (blobUrls.get(track.artworkRef) ?? null) : null;
}

/** Для Media Session браузера годится та же `blob:`-ссылка. */
export const artworkPath = artworkUri;

/**
 * Синяя нота для трека без обложки (см. `musicLibrary.ts`); браузеру хватает `data:`-ссылки.
 * Подгружается лениво: провайдер плеера стоит в корне, и 13 КБ картинки иначе легли бы в
 * основной бандл, у которого бюджет размера.
 */
export async function fallbackArtworkPath(): Promise<string | null> {
  const { PLACEHOLDER_ARTWORK_JPEG_BASE64 } = await import('./placeholderArtwork');
  return `data:image/jpeg;base64,${PLACEHOLDER_ARTWORK_JPEG_BASE64}`;
}
